/*
 * telekit (MIT, © 2026 Lulzx) components, ported from string templates to React.
 * iOS grouped-table view as Telegram draws it: large title, grouped sections, rows
 * with glyphs, values and chevrons, stat tiles, cards, chips, notices.
 */
import clsx from "clsx";
import { useId, useLayoutEffect, useRef, type ReactNode, type ButtonHTMLAttributes, type AnchorHTMLAttributes } from "react";
import { Link } from "react-router-dom";
import { haptic } from "../../lib/tg";
import { avatarColor, initials } from "../../lib/format";
import { Chevron, I, type IconName } from "./icons";

export { I, Chevron, BackChevron, BeaconMark } from "./icons";
export type { IconName } from "./icons";

/* ---------------- structure ---------------- */

export function TitleBar({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="tk-title">
      <div className="tk-title-text">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 pb-1">{actions}</div>}
    </header>
  );
}

export function GroupHeader({ children, action, id }: { children: ReactNode; action?: ReactNode; id?: string }) {
  return (
    <h2 className="tk-group-header" id={id}>
      <span>{children}</span>
      {action && <span className="tk-gh-action">{action}</span>}
    </h2>
  );
}
export function GroupFooter({ children }: { children: ReactNode }) {
  return <p className="tk-group-footer">{children}</p>;
}

/** A grouped section with optional header and footer (the iOS "inset grouped" table). */
export function Section({ header, headerAction, footer, children, className, style }: { header?: ReactNode; headerAction?: ReactNode; footer?: ReactNode; children: ReactNode; className?: string; style?: React.CSSProperties }) {
  const id = useId();
  return (
    <section aria-labelledby={header ? id : undefined}>
      {header && <GroupHeader id={id} action={headerAction}>{header}</GroupHeader>}
      <div className={clsx("tk-section", className)} style={style}>{children}</div>
      {footer && <GroupFooter>{footer}</GroupFooter>}
    </section>
  );
}

export const Gap = ({ h = 16 }: { h?: number }) => <div style={{ height: h }} aria-hidden="true" />;

/* ---------------- glyphs & avatars ---------------- */

export const GLYPH_COLORS = {
  blue: "#3390ec",
  green: "#31b545",
  orange: "#f09a37",
  red: "#e53935",
  purple: "#8e6cf0",
  teal: "#22b3c9",
  gray: "#8e8e93",
  indigo: "#5b6ef0",
  pink: "#e8558b",
  yellow: "#e8b100",
} as const;
export type GlyphColor = keyof typeof GLYPH_COLORS;

export function Glyph({ icon, color = "blue", size = 30, round }: { icon: IconName; color?: GlyphColor | string; size?: number; round?: boolean }) {
  const Icon = I[icon];
  const bg = color in GLYPH_COLORS ? GLYPH_COLORS[color as GlyphColor] : color;
  return (
    <span className="tk-glyph" style={{ background: bg, width: size, height: size, borderRadius: round ? "50%" : size * 0.27 }} aria-hidden="true">
      <Icon style={{ width: size * 0.6, height: size * 0.6 }} />
    </span>
  );
}

export function Avatar({ name, src, size = 30, seed }: { name: string; src?: string | null; size?: number; seed?: string }) {
  return (
    <span
      className="tk-avatar"
      style={{ width: size, height: size, background: src ? undefined : avatarColor(seed ?? name), fontSize: size * 0.4 }}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="" /> : initials(name)}
    </span>
  );
}

/* ---------------- rows ---------------- */

type RowBase = {
  label: ReactNode;
  sub?: ReactNode;
  value?: ReactNode;
  glyph?: ReactNode;
  after?: ReactNode;
  chevron?: boolean;
  destructive?: boolean;
  accent?: boolean;
  wrap?: boolean;
  /** Label and sub wrap to at most two lines each (feeds, alerts) instead of one truncated line. */
  clamp?: boolean;
  selected?: boolean;
  disabled?: boolean;
  topAligned?: boolean;
  inset?: "sm" | "lg";
  className?: string;
  mono?: boolean;
};
type RowAsLink = RowBase & { to: string; onClick?: never; href?: never };
type RowAsButton = RowBase & { onClick: () => void; to?: never; href?: never; ariaPressed?: boolean };
type RowAsHref = RowBase & { href: string; onClick?: never; to?: never };
type RowStatic = RowBase & { to?: undefined; onClick?: undefined; href?: undefined };

/** The telekit row: glyph · label/sub · value · accessory. Renders a link, a button, or a plain div. */
export function Row(props: RowAsLink | RowAsButton | RowAsHref | RowStatic) {
  const { label, sub, value, glyph, after, chevron, destructive, accent, wrap, clamp, selected, disabled, topAligned, inset, className, mono } = props;
  const tappable = !!(props.to || props.onClick || props.href);
  const cls = clsx(
    "tk-row",
    tappable && "tk-tap",
    destructive && "tk-destructive",
    accent && "tk-accent-label",
    selected && "tk-selected",
    disabled && "tk-disabled",
    topAligned && "tk-top-aligned",
    (inset === "sm" || (glyph && !inset)) && "tk-inset",
    inset === "lg" && "tk-inset-lg",
    className,
  );
  const body = (
    <>
      {glyph}
      <span className="tk-rowbody">
        <span className={clsx("tk-label", wrap && "tk-wrap-text", clamp && "tk-clamp")}>{label}</span>
        {sub && <span className={clsx("tk-sub", wrap && "tk-wrap-text", clamp && "tk-clamp")}>{sub}</span>}
      </span>
      {value != null && value !== false && <span className={clsx("tk-value", mono && "tk-mono tk-num")}>{value}</span>}
      {after}
      {(chevron ?? (!!props.to && !after)) && <Chevron />}
    </>
  );
  if (props.to) {
    return (
      <Link to={props.to} className={cls} aria-current={selected ? "page" : undefined} onClick={() => haptic.select()}>
        {body}
      </Link>
    );
  }
  if (props.href) {
    return (
      <a href={props.href} className={cls} target="_blank" rel="noopener noreferrer">
        {body}
      </a>
    );
  }
  if (props.onClick) {
    const onClick = props.onClick;
    return (
      <button
        type="button"
        className={cls}
        disabled={disabled}
        aria-pressed={"ariaPressed" in props ? props.ariaPressed : undefined}
        onClick={() => {
          haptic.select();
          onClick();
        }}
      >
        {body}
      </button>
    );
  }
  return <div className={cls}>{body}</div>;
}

/** A row with a native switch; the whole row is the hit target. */
export function SwitchRow({ label, sub, glyph, checked, onChange, disabled, accent }: { label: ReactNode; sub?: ReactNode; glyph?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; accent?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={clsx("tk-row tk-tap", glyph && "tk-inset", disabled && "tk-disabled")}
      onClick={() => {
        haptic.tap();
        onChange(!checked);
      }}
    >
      {glyph}
      <span className="tk-rowbody">
        <span className="tk-label">{label}</span>
        {sub && <span className="tk-sub tk-wrap-text">{sub}</span>}
      </span>
      <span className={clsx("tk-switch", checked && "tk-on", accent && "tk-accent")} aria-hidden="true" />
    </button>
  );
}
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={clsx("tk-switch", checked && "tk-on")}
      onClick={() => {
        haptic.tap();
        onChange(!checked);
      }}
    />
  );
}

/** Single-choice list — iOS checkmark rows. */
export function PickRows<T extends string>({ options, value, onChange, name }: { options: { value: T; label: ReactNode; sub?: ReactNode; disabled?: boolean }[]; value: T; onChange: (v: T) => void; name: string }) {
  return (
    <div role="radiogroup" aria-label={name}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={o.disabled}
          className={clsx("tk-row tk-tap", o.disabled && "tk-disabled")}
          onClick={() => {
            haptic.select();
            onChange(o.value);
          }}
        >
          <span className="tk-rowbody">
            <span className="tk-label">{o.label}</span>
            {o.sub && <span className="tk-sub tk-wrap-text">{o.sub}</span>}
          </span>
          <span className={clsx("tk-check tk-pick", o.value === value && "tk-on")} aria-hidden="true">
            <I.check />
          </span>
        </button>
      ))}
    </div>
  );
}

/** Text input laid out as a row: label on the left, field on the right (or a stacked field). */
export function FieldRow({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputMode,
  suffix,
  stacked,
  multiline,
  maxLength,
  counter,
  error,
  autoFocus,
  mono,
  name,
}: {
  label: ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  suffix?: ReactNode;
  stacked?: boolean;
  multiline?: boolean;
  maxLength?: number;
  counter?: ReactNode;
  error?: string | null;
  autoFocus?: boolean;
  mono?: boolean;
  name?: string;
}) {
  const id = useId();
  const field = multiline ? (
    <textarea id={id} name={name} className={clsx("tk-field", mono && "tk-mono")} rows={3} value={value} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus} aria-invalid={!!error} />
  ) : (
    <input
      id={id}
      name={name}
      className={clsx("tk-field", !stacked && "text-right", mono && "tk-mono")}
      type={type}
      inputMode={inputMode}
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      autoFocus={autoFocus}
      aria-invalid={!!error}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  if (stacked || multiline) {
    return (
      <div className="tk-row tk-top-aligned" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
        <div className="flex items-baseline gap-2">
          <label htmlFor={id} className="text-[13px] text-[var(--tk-subtitle)] flex-1">
            {label}
          </label>
          {counter}
        </div>
        {field}
        {error && <span className="text-[13px] text-red">{error}</span>}
      </div>
    );
  }
  return (
    <div className="tk-row">
      <label htmlFor={id} className="tk-label tk-clamp" style={{ flex: "0 1 auto", maxWidth: "65%" }}>
        {label}
      </label>
      <span className="flex-1 min-w-[72px] flex items-center justify-end gap-1">
        {field}
        {suffix && <span className="tk-hint flex-none">{suffix}</span>}
      </span>
      {error && <span className="sr-only">{error}</span>}
    </div>
  );
}

/* ---------------- chips & badges ---------------- */

type BadgeTone = "default" | "accent" | "red" | "green" | "orange" | "purple" | "solid" | "muted-solid";
export function Badge({ children, tone = "default", sm, className, title }: { children: ReactNode; tone?: BadgeTone; sm?: boolean; className?: string; title?: string }) {
  return (
    <span title={title} className={clsx("tk-badge", tone !== "default" && `tk-${tone}`, sm && "tk-sm", className)}>
      {children}
    </span>
  );
}
export function Counter({ n, muted, red, label }: { n: number; muted?: boolean; red?: boolean; label?: string }) {
  if (n <= 0) return null;
  return (
    <span className={clsx("tk-counter", muted && "tk-muted", red && "tk-red")} aria-label={label ?? `${n} unread`}>
      {n > 99 ? "99+" : n}
    </span>
  );
}
export function ModeledTag({ title = "Modeled: estimated, not observed" }: { title?: string }) {
  return (
    <span className="modeled-tag" title={title}>
      modeled
    </span>
  );
}

type ChipTone = "default" | "primary" | "danger" | "danger-solid" | "plain";
type ChipProps = ButtonHTMLAttributes<HTMLButtonElement> & { tone?: ChipTone; auto?: boolean; lg?: boolean; icon?: IconName; loading?: boolean; hapticStyle?: "light" | "medium" | "heavy" };
/** telekit's pill button. `auto` sizes to content instead of filling the row. */
export function Chip({ tone = "default", auto, lg, icon, loading, className, children, onClick, hapticStyle = "light", disabled, ...rest }: ChipProps) {
  const Icon = icon ? I[icon] : null;
  return (
    <button
      type="button"
      className={clsx("tk-chip", tone !== "default" && `tk-${tone}`, auto && "tk-auto", lg && "tk-lg", className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={(e) => {
        haptic.tap(hapticStyle);
        onClick?.(e);
      }}
      {...rest}
    >
      {loading ? <span className="tk-ring tk-sm tk-light" aria-hidden="true" /> : Icon && <Icon width={17} height={17} />}
      {children}
    </button>
  );
}
export function ChipLink({ tone = "default", auto, icon, className, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { tone?: ChipTone; auto?: boolean; icon?: IconName }) {
  const Icon = icon ? I[icon] : null;
  return (
    <a className={clsx("tk-chip", tone !== "default" && `tk-${tone}`, auto && "tk-auto", className)} target="_blank" rel="noopener noreferrer" {...rest}>
      {Icon && <Icon width={17} height={17} />}
      {children}
    </a>
  );
}

/** Filter chips (status chips in tables) — Telegram's folder-tab pills. */
export function FilterChips<T extends string>({ items, value, onChange, label }: { items: { value: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const i = items.findIndex((it) => it.value === value);
  // Keep the selected chip in view when the strip scrolls sideways. Only the strip moves, never the page.
  useLayoutEffect(() => {
    const el = ref.current;
    const b = el?.children[i] as HTMLElement | undefined;
    if (!el || !b || el.scrollWidth <= el.clientWidth) return;
    const pad = 16;
    const l = b.offsetLeft - el.scrollLeft;
    if (l < pad) el.scrollLeft = b.offsetLeft - pad;
    else if (l + b.offsetWidth > el.clientWidth - pad) el.scrollLeft = b.offsetLeft + b.offsetWidth - el.clientWidth + pad;
  }, [i, items.length]);
  return (
    <div ref={ref} className="hscroll tk-filters" role="tablist" aria-label={label}>
      {items.map((it) => {
        const on = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => {
              haptic.select();
              onChange(it.value);
            }}
            className={clsx(
              "tk-filter h-8 px-3 rounded-full text-[14px] font-medium inline-flex items-center gap-1.5 transition-colors",
              on ? "bg-accent text-[var(--tk-accent-text)]" : "bg-[var(--chip-off,var(--tk-section))] text-ink hover:bg-[color-mix(in_srgb,var(--tk-hint)_12%,var(--chip-off,var(--tk-section)))]",
            )}
          >
            {it.label}
            {it.count != null && <span className={clsx("text-[12px] tabular-nums", !on && "text-ink-3")}>{it.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** iOS segmented control: equal segments with a thumb that slides to the selected one. */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
  inline,
  disabled,
  className,
}: {
  /** `title` explains a disabled item (shown as a tooltip). */
  items: { value: T; label: ReactNode; disabled?: boolean; title?: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  inline?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const i = items.findIndex((it) => it.value === value);
  const ref = useRef<HTMLDivElement>(null);
  // The thumb follows the selected button's measured box, so segments can differ in width.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const place = () => {
      const b = el.children[i] as HTMLElement | undefined;
      if (!b) return;
      el.style.setProperty("--seg-x", `${b.offsetLeft}px`);
      el.style.setProperty("--seg-w", `${b.offsetWidth}px`);
      if (!el.classList.contains("tk-measured")) {
        el.classList.add("tk-measured");
        requestAnimationFrame(() => el.classList.add("tk-ready"));
      }
      if (el.scrollWidth > el.clientWidth) {
        const l = b.offsetLeft - el.scrollLeft;
        if (l < 0 || l + b.offsetWidth > el.clientWidth) el.scrollTo({ left: b.offsetLeft - (el.clientWidth - b.offsetWidth) / 2 });
      }
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    for (const c of el.children) ro.observe(c);
    return () => ro.disconnect();
  }, [i, items.length]);
  return (
    <div ref={ref} className={clsx("tk-segmented tk-slide", inline && "tk-inline", i < 0 && "tk-none", disabled && "tk-disabled", className)} role="radiogroup" aria-label={label} aria-disabled={disabled || undefined}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="radio"
          aria-checked={it.value === value}
          className={clsx(it.value === value && "tk-on")}
          disabled={disabled || it.disabled}
          title={it.title}
          onClick={() => {
            haptic.select();
            onChange(it.value);
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- status ---------------- */

export function Hero({ dial, progress, title, detail, off, warn, size = 76 }: { dial: ReactNode; progress: number; title: ReactNode; detail?: ReactNode; off?: boolean; warn?: boolean; size?: number }) {
  const r = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(1, Math.max(0, progress)));
  return (
    <div className="tk-hero">
      <div className={clsx("tk-dial", off && "tk-off", warn && "tk-warn")} style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle className="tk-track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth="6" />
          <circle className="tk-fill" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth="6" strokeDasharray={circ.toFixed(1)} strokeDashoffset={offset.toFixed(1)} />
        </svg>
        <div className="tk-center">{dial}</div>
      </div>
      <div className="tk-rowbody">
        <div className="tk-state">{title}</div>
        {detail && <div className="tk-detail">{detail}</div>}
      </div>
    </div>
  );
}

export function Tiles({ items, cols }: { items: { n: ReactNode; k: ReactNode; onClick?: () => void; left?: boolean }[]; cols?: number }) {
  return (
    <div className="tk-tiles" style={{ ["--tk-tile-cols" as string]: cols ?? items.length }}>
      {items.map((t, i) =>
        t.onClick ? (
          <button key={i} type="button" className={clsx("tk-tile", t.left && "tk-left")} onClick={t.onClick}>
            <div className="tk-n">{t.n}</div>
            <div className="tk-k">{t.k}</div>
          </button>
        ) : (
          <div key={i} className={clsx("tk-tile", t.left && "tk-left")}>
            <div className="tk-n">{t.n}</div>
            <div className="tk-k">{t.k}</div>
          </div>
        ),
      )}
    </div>
  );
}

export function Card({ children, className, style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={clsx("tk-card", className)} style={style}>{children}</div>;
}

type NoticeTone = "info" | "warn" | "accent" | "caution" | "positive";
const NOTICE_ICON: Record<NoticeTone, IconName> = { info: "info", warn: "alert", accent: "info", caution: "alert", positive: "check" };
export function Notice({ tone = "info", children, icon, onClick, action, className }: { tone?: NoticeTone; children: ReactNode; icon?: IconName; onClick?: () => void; action?: ReactNode; className?: string }) {
  const Icon = I[icon ?? NOTICE_ICON[tone]];
  const inner = (
    <>
      <span className="tk-notice-icon">
        <Icon />
      </span>
      <span className="tk-notice-body">{children}</span>
      {action}
      {onClick && <Chevron />}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={clsx("tk-notice", `tk-${tone}`, className)} onClick={onClick}>
        {inner}
      </button>
    );
  }
  return (
    <div className={clsx("tk-notice", `tk-${tone}`, className)} role={tone === "warn" ? "alert" : undefined}>
      {inner}
    </div>
  );
}

export function Progress({ value, label, right, tone, indeterminate }: { value: number; label?: ReactNode; right?: ReactNode; tone?: "warn" | "danger"; indeterminate?: boolean }) {
  return (
    <div className={clsx("tk-progress", tone && `tk-${tone}`, indeterminate && "tk-indeterminate")}>
      {(label || right) && (
        <div className="tk-progress-label">
          <span>{label}</span>
          <span className="tk-num">{right}</span>
        </div>
      )}
      <div className="tk-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={indeterminate ? undefined : Math.round(value * 100)}>
        <div className="tk-progress-fill" style={indeterminate ? undefined : { transform: `scaleX(${Math.min(1, Math.max(0, value))})` }} />
      </div>
    </div>
  );
}

export function SearchField({ value, onChange, placeholder = "Search", kbd, autoFocus, inputRef, onKeyDown, className }: { value: string; onChange: (v: string) => void; placeholder?: string; kbd?: string; autoFocus?: boolean; inputRef?: React.Ref<HTMLInputElement>; onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>; className?: string }) {
  return (
    <label className={clsx("tk-search", className)}>
      <I.search />
      <input
        ref={inputRef}
        className="tk-field"
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        onChange={(e) => onChange(e.target.value)}
      />
      {kbd && <kbd>{kbd}</kbd>}
    </label>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="tk-loading" role="status">
      <span className="tk-ring" aria-hidden="true" />
      {label}
    </div>
  );
}
export function Ring({ sm, light }: { sm?: boolean; light?: boolean }) {
  return <span className={clsx("tk-ring", sm && "tk-sm", light && "tk-light")} aria-hidden="true" />;
}

export function Empty({ icon = "inbox", title, children, action }: { icon?: IconName; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const Icon = I[icon];
  return (
    <div className="tk-empty">
      <div className="tk-art">
        <Icon />
      </div>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Skeleton({ h = 74, n = 1 }: { h?: number; n?: number }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="tk-skeleton" style={{ height: h }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof Error ? error.message : "Something went wrong";
  return (
    <Empty icon="alert" title="Couldn’t load this" action={retry && <Chip auto onClick={retry}>Try again</Chip>}>
      {msg}
    </Empty>
  );
}

/** Dot + label status for sync freshness, ad status, etc. */
export function StatusDot({ tone, children }: { tone: "green" | "orange" | "red" | "gray" | "accent"; children?: ReactNode }) {
  const c = tone === "green" ? "var(--green)" : tone === "orange" ? "var(--orange)" : tone === "red" ? "var(--red)" : tone === "accent" ? "var(--accent)" : "var(--ink-3)";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block size-2 rounded-full flex-none" style={{ background: c }} aria-hidden="true" />
      {children}
    </span>
  );
}
